import { makeAutoObservable, runInAction } from 'mobx';
import { registrationService } from '../services/registrationService';
import type { Registration, SendMessageRequest } from '../types';

class RegistrationStore {
  registrations: Registration[] = [];
  currentRegistration: Registration | null = null;
  statistics = {
    todayCount: 0,
    waitingCount: 0,
  };
  isLoading = false;
  error: string | null = null;
  // Individual loading states for statistics
  loadingStates = {
    todayRegistrations: false,
    todayWaitingGuests: false,
  };
  pagination = {
    page: 1,
    limit: 50,
    totalItems: 0,
    totalPages: 0,
  };
  filters = {
    status: '',
    fromDate: '',
    toDate: '',
    callContext: '',
    isActive: null as boolean | null,
    searchType: 'callNumber' as 'callNumber' | 'lineUserId',
    searchValue: '',
  };

  constructor() {
    makeAutoObservable(this);
  }

  async fetchRegistrations() {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await registrationService.getRegistrations({
        page: this.pagination.page,
        limit: this.pagination.limit,
        ...this.filters,
      });

      runInAction(() => {
        this.registrations = response.items;
        this.pagination = {
          page: response.page,
          limit: response.limit,
          totalItems: response.totalItems,
          totalPages: response.totalPages,
        };
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || '登録データの取得に失敗しました';
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async fetchStatistics() {
    try {
      const stats = await registrationService.getStatistics();
      runInAction(() => {
        this.statistics = {
          todayCount: stats.todayCount || 0,
          waitingCount: stats.waitingCount || 0,
        };
      });
    } catch (error: any) {
      console.error('Failed to fetch registration statistics:', error);
    }
  }

  // Individual statistics fetch methods
  async fetchTodayRegistrationsCount() {
    runInAction(() => {
      this.loadingStates.todayRegistrations = true;
    });

    try {
      const result = await registrationService.getTodayRegistrationsCount();
      runInAction(() => {
        this.statistics.todayCount = result.count;
        this.loadingStates.todayRegistrations = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.loadingStates.todayRegistrations = false;
      });
      console.error('Failed to fetch today registrations count:', error);
    }
  }

  async fetchTodayWaitingGuestsCount() {
    runInAction(() => {
      this.loadingStates.todayWaitingGuests = true;
    });

    try {
      const result = await registrationService.getTodayWaitingGuestsCount();
      runInAction(() => {
        this.statistics.waitingCount = result.count;
        this.loadingStates.todayWaitingGuests = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.loadingStates.todayWaitingGuests = false;
      });
      console.error('Failed to fetch today waiting guests count:', error);
    }
  }

  async sendMessage(request: SendMessageRequest) {
    this.isLoading = true;
    this.error = null;
    
    try {
      await registrationService.sendMessage(request);
      
      // Update registration status if provided
      if (request.registrationId && request.status) {
        runInAction(() => {
          const registration = this.registrations.find(r => r.id === request.registrationId);
          if (registration) {
            registration.status = request.status as any;
          }
        });
      }
      
      return true;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || 'メッセージの送信に失敗しました';
      });
      return false;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  setPage(page: number) {
    this.pagination.page = page;
    this.fetchRegistrations();
  }

  setFilter(key: keyof typeof this.filters, value: string | boolean | null) {
    (this.filters as any)[key] = value;
    this.pagination.page = 1;
    this.fetchRegistrations();
  }

  setFilterWithoutFetch(key: keyof typeof this.filters, value: string | boolean | null) {
    (this.filters as any)[key] = value;
  }

  setSearchFilter(searchType: 'callNumber' | 'lineUserId', searchValue: string) {
    this.filters.searchType = searchType;
    this.filters.searchValue = searchValue;
    this.pagination.page = 1;
    this.fetchRegistrations();
  }

  clearFilters() {
    const today = new Date().toISOString().split('T')[0];
    this.filters = {
      status: '',
      fromDate: today,
      toDate: today,
      callContext: '',
      isActive: null,
      searchType: 'callNumber',
      searchValue: '',
    };
    this.pagination.page = 1;
    this.fetchRegistrations();
  }

  async deleteRegistration(registrationId: string) {
    this.isLoading = true;
    this.error = null;

    try {
      await registrationService.deleteRegistration(registrationId);

      // Remove from local state
      runInAction(() => {
        this.registrations = this.registrations.filter(r => r.id !== registrationId);
        this.pagination.totalItems = Math.max(0, this.pagination.totalItems - 1);
      });

      return true;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || '登録の削除に失敗しました';
      });
      return false;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  get waitingRegistrationsCount() {
    return this.statistics.waitingCount;
  }

  get todayRegistrationsCount() {
    return this.statistics.todayCount;
  }
}

export const registrationStore = new RegistrationStore();